import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Card, Callout, Reveal, PullQuote, StatBlock } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { AAID_PILLARS, DELIVERY_GAPS, PILOT_PHASES, GUARDRAILS, REPLICATION_STEPS } from '../data/government';
export function PublicDelivery() {
  return <>
      <section className="surface-dark" data-id="element-1131" data-mp="2dmnsob7etqpb">
        <Container className="py-14 sm:py-20" data-id="element-1132" data-mp="2dnpe99m1xvla">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1133" data-mp="1zmmreun8gh9b">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-1134" data-mp="1zo7jmdm2ro40">
              <li data-id="element-1135" data-mp="24lzmu3s66wni"><Link to="/" className="hover:text-white" data-id="element-1136" data-mp="24lzl9zciuole">Home</Link></li>
              <li aria-hidden="true" data-id="element-1137" data-mp="3u1fkalwqetxk"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1138" data-mp="3u0xeoyo6j3cz" /></li>
              <li data-id="element-1139" data-mp="2umhxeq7corg"><Link to="/services" className="hover:text-white" data-id="element-1140" data-mp="2ucthvkt2pnv">Services</Link></li>
              <li aria-hidden="true" data-id="element-1141" data-mp="3bl28gxwqxn3q"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1142" data-mp="3bk90jtm1wrk0" /></li>
              <li aria-current="page" className="text-white/80" data-id="element-1143" data-mp="37jp3eq1h9nxv">Public delivery</li>
            </ol>
          </nav>
          <Kicker data-id="element-1144" data-mp="3lbn6ok8icpm4">Cavalla Public Delivery, pilot programme</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-[34px] leading-tight text-white sm:text-[48px]" data-id="element-1145" data-mp="3ld84qju16qoq">
            A delivery dashboard for the ARREST Agenda
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-1146" data-mp="2smyc741yjijc">
            Liberia has adopted a five year plan that measures itself. We propose to stand up the instrument that
            reads it: one agreed baseline, each programme budget line joined to the outcome it is meant to move,
            and an assisted early warning on slippage, in ninety days.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-1147" data-mp="hsk86y9jkjt4">
            <Button to="/contact" size="lg" shape="pill" data-id="element-1148" data-mp="cu8x6k7ig39b">
              Request the pilot brief
            </Button>
            <Button to="/augmented-intelligence" variant="reversed" size="lg" shape="pill" data-id="element-1149" data-mp="kvgio4tclzjz">
              Read our method
            </Button>
          </div>
        </Container>
      </section>

      <Section aria-labelledby="why-now" data-id="element-1150" data-mp="txzj6i9nw032">
        <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr]" data-id="element-1151" data-mp="yvrapb6e9k6q">
          <div data-id="element-1152" data-mp="yuzqsxm5tawa">
            <SectionHeader id="why-now" kicker="Why this is timely" title="The plan is only measurable if year one is instrumented" data-id="element-1153" data-mp="ytokaq1bxp1h" />
            <div className="mt-6 max-w-prose space-y-5 text-[16px] text-body" data-id="element-1154" data-mp="67dbis3kkhtl">
              <p data-id="element-1155" data-mp="b5exbksdqv8r">
                The ARREST Agenda for Inclusive Development is Liberia&rsquo;s fourth post conflict national
                development plan and runs from 2025 to 2029. It is built on six pillars and, for the first time,
                on a development drivers and filters approach with a cause and consequence method, so that
                interventions are judged on measurable cross sectoral outcomes.
              </p>
              <p data-id="element-1156" data-mp="373eauptgkmla">
                It also commits to a programme based approach that allows programme based budgeting, tying
                development outcomes to the national budget, and to the use of technology to improve public
                service delivery while resource leakage through corruption is stamped out.
              </p>
              <p data-id="element-1157" data-mp="2t4ocl6uwkgvc">
                Each of those commitments needs a baseline set in the first budget cycle. A baseline agreed in
                year three describes a plan that is already two years old. That is the window, and it is the
                reason we are proposing this now rather than at the mid term review.
              </p>
            </div>
          </div>

          <div className="space-y-6 rounded-md border border-line bg-mist p-7" data-id="element-1158" data-mp="29g5l2imfcrdh">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-1159" data-mp="25e0rfjvz7uqu">
              From the plan
            </p>
            <div className="grid gap-8 sm:grid-cols-2" data-id="element-1160" data-mp="2u5pyfb5r3e06">
              <StatBlock value={6} label="Pillars the plan must report against" data-id="element-1161" data-mp="2u714xiqkyzuz" />
              <StatBlock value={5.9} decimals={1} suffix="%" label="Average annual GDP growth targeted" data-id="element-1162" data-mp="2z5vud3j1zmo2" />
              <StatBlock value={1050.3} decimals={1} prefix="$" label="GDP per capita target" data-id="element-1163" data-mp="2laa6izok38x8" />
              <StatBlock value={485} suffix=" km" label="Minimum primary roads to be paved" data-id="element-1164" data-mp="2pbvfwb1vt52o" />
            </div>
            <p className="text-small text-muted" data-id="element-1165" data-mp="2bgjn497adooe">
              Figures quoted from the ARREST Agenda for Inclusive Development, National Development Plan 2025 to
              2029. The plan also records that 45 per cent of the population lives in multidimensional poverty
              and that 74.6 per cent is under 35.
            </p>
          </div>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="gap" data-id="element-1166" data-mp="k1c3clodato">
        <SectionHeader id="gap" kicker="The gap we close" title="Four things that stop a good plan from being reportable" lead="None of these is a failure of intent. They are what happens to any plan of this size without a standing instrument behind it." data-id="element-1167" data-mp="4o0s7u31m7mi" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-1168" data-mp="1ywmpg21xrn8t">
          {DELIVERY_GAPS.map((gap, index) => <Reveal key={gap.title} delay={index * 0.05} className="h-full" data-id="element-1169" data-mp="1u3thz6f320t1" data-mpi={index}>
              <div className="h-full border-l-2 border-gold bg-white p-6" data-id="element-1170" data-mp="28t960en8bs51">
                <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-1171" data-mp="23uo6khfsxchn">{gap.title}</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1172" data-mp="2hugu63w17cle">{gap.detail}</p>
              </div>
            </Reveal>)}
        </div>
      </Section>

      <Section aria-labelledby="pilot" data-id="element-1173" data-mp="2cwnp5e6gsomp">
        <SectionHeader id="pilot" kicker="The pilot" title="Ninety days, three phases, one working instrument" lead="Scoped so that the government holds something usable at the end of each thirty day window, not only at the end." data-id="element-1174" data-mp="2ctjatnzaccib" />
        <ol className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-1175" data-mp="2ilfcdh0tjybo">
          {PILOT_PHASES.map((phase, index) => <li key={phase.window} data-id="element-1176" data-mp="2nkryfzjv91d7" data-mpi={index}>
              <Card className="flex h-full flex-col" data-id="element-1177" data-mp="2sh8udfxb4k0u">
                <p className="font-display text-[13px] text-gold" data-id="element-1178" data-mp="2sjv15dcdivi0">0{index + 1}</p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1179" data-mp="2wkx1jxi11dlk">
                  {phase.window}
                </p>
                <h3 className="mt-3 font-display text-[23px] leading-snug text-ink" data-id="element-1180" data-mp="36h03p3cvpc8i">{phase.title}</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1181" data-mp="3b7qa8jx1jl4s">{phase.detail}</p>
                <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1182" data-mp="3g7cxznahwjru">
                  You receive
                </p>
                <ul className="mt-3 space-y-3 text-[14px] text-body" data-id="element-1183" data-mp="3ka98eqpvt9kp">
                  {phase.deliverables.map(item => <li key={item} className="flex gap-3" data-id="element-1184" data-mp="3p5ydtaol1kox">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1185" data-mp="1dlqrjisbl6jg" />
                      {item}
                    </li>)}
                </ul>
              </Card>
            </li>)}
        </ol>
        <Callout className="mt-8" data-id="element-1186" data-mp="14lu8pxsm31wb">
          The pilot runs on the reporting the government already collects. It does not require a new statistical
          survey, a new ministry system or a change to the budget calendar to produce its first output.
        </Callout>
      </Section>

      <Section tone="dark" aria-labelledby="pillars" data-id="element-1187" data-mp="1sboksd53wmfi">
        <SectionHeader id="pillars" reversed kicker="Coverage" title="Six pillars, one indicator each to begin" lead="We start narrow on purpose. Twelve leading indicators read weekly are worth more than two hundred read once a year." data-id="element-1188" data-mp="1nhttanj9f35v" />
        <div className="mt-10 grid gap-px bg-white/15 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1189" data-mp="2b867uh90fy5f">
          {AAID_PILLARS.map(pillar => <article key={pillar.name} className="flex flex-col bg-[#0d2c53] p-7" data-id="element-1190" data-mp="1jja7f5c99tmo">
              <h3 className="font-display text-[21px] leading-snug text-white" data-id="element-1191" data-mp="1ocd7zxfga3cd">{pillar.name}</h3>
              <p className="mt-3 text-[14px] text-white/68" data-id="element-1192" data-mp="19o07qo9bxyc2">{pillar.focus}</p>
              <div className="mt-auto pt-6" data-id="element-1193" data-mp="1el0el1e07548">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gold" data-id="element-1194" data-mp="1em1q16yxltmh">
                  First indicator
                </p>
                <p className="mt-2 text-[15px] text-white" data-id="element-1195" data-mp="27az5l2brdcbj">{pillar.firstIndicator}</p>
                <p className="mt-2 text-small text-white/45" data-id="element-1196" data-mp="1tay69rb56cy6">{pillar.baseline}</p>
              </div>
            </article>)}
        </div>
      </Section>

      <Section aria-labelledby="guardrails" data-id="element-1197" data-mp="1xagjs20mvlqz">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]" data-id="element-1198" data-mp="huzsja50r2qm">
          <div data-id="element-1199" data-mp="hxvs12wilzh6">
            <SectionHeader id="guardrails" kicker="Governance" title="Where the AI sits, and where it does not" data-id="element-1200" data-mp="lwutgdj1fe3v" />
            <p className="mt-6 max-w-md text-[16px] text-body" data-id="element-1201" data-mp="efojfuxkqkw7">
              The assisted layer reads ministry returns, normalises them and flags movement against the baseline.
              It writes no decision and it signs no report. A named official does both.
            </p>
            <div className="mt-8" data-id="element-1202" data-mp="jd7xouu9uxo5">
              <Button to="/services/ai" variant="secondary" shape="pill" data-id="element-1203" data-mp="3rshubnfe7aoj">
                Read about Cavalla AI
              </Button>
            </div>
          </div>
          <ul className="space-y-4" data-id="element-1204" data-mp="3muye4hz4wnty">
            {GUARDRAILS.map(rule => <li key={rule} className="flex gap-4 border-b border-line pb-4 text-[16px] text-body last:border-0" data-id="element-1205" data-mp="5dypahcb57uq">
                <ShieldCheckIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-1206" data-mp="km7aq24vzay" />
                {rule}
              </li>)}
          </ul>
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="replication" data-id="element-1207" data-mp="342e36fje53ef">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr]" data-id="element-1208" data-mp="h1sbn4bkrhq7">
          <div data-id="element-1209" data-mp="gyxupmrrsf1j">
            <SectionHeader id="replication" kicker="After the pilot" title="Built once in Liberia, offered to every government with a plan" data-id="element-1210" data-mp="czoymb99c52t" />
            <p className="mt-6 max-w-md text-[16px] text-body" data-id="element-1211" data-mp="31hly4m0496m">
              We are treating Liberia as the reference implementation. The instrument is deliberately generic
              beneath the indicator layer, because the reporting duty it serves is common to every national
              development plan aligned to the Sustainable Development Goals, Agenda 2063, ECOWAS Agenda 2050 and
              the African Continental Free Trade Area.
            </p>
            <PullQuote className="mt-8" data-id="element-1212" data-mp="3gi8kmw1ceep6">
              A published quarterly review is a better sales document than any proposal we could write.
            </PullQuote>
          </div>
          <ol className="space-y-6" data-id="element-1213" data-mp="36jj3j5h9sy9z">
            {REPLICATION_STEPS.map((step, index) => <li key={step.title} className="grid gap-4 border-t border-line pt-6 sm:grid-cols-[48px_1fr]" data-id="element-1214" data-mp="2wdhpmci9pg3k" data-mpi={index}>
                <span className="font-display text-[24px] leading-none text-gold" data-id="element-1215" data-mp="31bt26uzy64ph">0{index + 1}</span>
                <div data-id="element-1216" data-mp="36d080oij4tti">
                  <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-1217" data-mp="36amfonf8hvpn">{step.title}</h3>
                  <p className="mt-2 text-[15px] text-body" data-id="element-1218" data-mp="3b9h5487piiiq">{step.detail}</p>
                </div>
              </li>)}
          </ol>
        </div>
      </Section>

      <Section aria-labelledby="terms" data-id="element-1219" data-mp="2ine9m39zeimk">
        <div className="grid gap-10 lg:grid-cols-2" data-id="element-1220" data-mp="2nlze4ew9nogt">
          <div data-id="element-1221" data-mp="2nkeh8imhbtfs">
            <SectionHeader id="terms" kicker="How to start" title="What we need from the government" data-id="element-1222" data-mp="2nko5o1rvlsjd" />
            <ul className="mt-6 space-y-3 text-[16px] text-body" data-id="element-1223" data-mp="2sesg4v0coip7">
              {['A sponsor at the centre of government, with authority across the six pillars', 'A named focal point in the ministry of finance and in each sector ministry', 'Access to the reporting returns already being collected', 'Agreement that the first quarterly review is published'].map(item => <li key={item} className="flex gap-3" data-id="element-1224" data-mp="21kgwbbqcnldl">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1225" data-mp="21ncu90276a21" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="rounded-md border border-line bg-mist p-7" data-id="element-1226" data-mp="18yxj4om6sbvz">
            <h3 className="font-display text-[22px] text-ink" data-id="element-1227" data-mp="13z1mxbjqdoqz">Scope, timing and fees</h3>
            <p className="mt-3 text-[16px] text-body" data-id="element-1228" data-mp="1iw244l4aq1b7">
              Set out in a written proposal after a discovery call, and structured so the pilot can be carried by
              a development partner grant or by the national budget. We do not publish fees for public sector
              work because scope depends on the reporting that already exists.
            </p>
            <p className="mt-4 text-small text-muted" data-id="element-1229" data-mp="2wu2nd7jwkv6f">
              Cavalla Solution is not a financial adviser and nothing on this page is financial or legal advice.
            </p>
            <div className="mt-6" data-id="element-1230" data-mp="2ntxh50qg42tu">
              <Button to="/contact" shape="pill" className="w-full" data-id="element-1231" data-mp="2nuz4a3kqkfrx">
                Request the pilot brief
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <section className="surface-dark" data-id="element-1232" data-mp="3fktsfwwuxzf0">
        <Container className="py-16" data-id="element-1233" data-mp="3fn6jr0oi9m4q">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center" data-id="element-1234" data-mp="3kkpwtx9gvsv3">
            <h2 className="max-w-2xl font-display text-[28px] leading-tight text-white sm:text-[36px]" data-id="element-1235" data-mp="36tbe7rjh1tok">
              If your government has adopted a plan, the first year is the one that decides whether it can be
              measured
            </h2>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row" data-id="element-1236" data-mp="2cgycl3u9st5">
              <Button to="/contact" size="lg" shape="pill" data-id="element-1237" data-mp="763bsfhtu0q0">
                Book a discovery call
              </Button>
              <Button to="/services" variant="reversed" size="lg" shape="pill" data-id="element-1238" data-mp="2fnnx49dhwv9w">
                All services
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>;
}